import { useQuery } from '@tanstack/react-query';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { VehicleSearchData } from '@/reports/types/report';
import { request } from '@/utils/index';

const vendorURI = '/hk/dd/v2';

export const useMarketplaceSearch = (searchData: VehicleSearchData) => {
  const {
    links: { marketplaceBaseUrl },
  } = useAppConstantsContext();
  const { logError } = useAppConfigContext();

  const V2_LISTINGS_ENDPOINT = `${vendorURI}/market/vehicle`;

  const result = useQuery({
    queryKey: ['marketplaceSearch'],
    queryFn: () => fetchSimilarVehiclesOnMarketplace(searchData),

    onError: (error) => {
      logError('Error fetching marketplace data', error as Error);
    },
  });

  const fetchSimilarVehiclesOnMarketplace = async (
    searchData: VehicleSearchData
  ) => {
    const queryData = generateQueryData(searchData);

    const response = await request(V2_LISTINGS_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(queryData),
    });

    const { listings, stats } = response.entities.market.vehicles;
    const obj = {
      marketplaceListings: listings?.length ? listings.slice(0, 6) : null,
      marketStats: stats,
      marketplaceURL: generateMarketplaceURL(searchData, marketplaceBaseUrl),
    };

    return obj;
  };

  return { ...result };
};

const generateQueryData = (searchData: VehicleSearchData) => {
  const { make, model, year } = searchData;

  return {
    makes: [make],
    models: [model],
    years: generateYearsRange(year),
  };
};

const generateYearsRange = (year: number | null) => {
  if (!year) return [];
  const years: number[] = [];

  if (!isNaN(year)) {
    const start = year - 5;
    const end = year + 5;
    for (let i = start; i <= end; i++) {
      years.push(i);
    }
  }

  return years;
};

const generateMarketplaceURL = (
  searchData: VehicleSearchData,
  marketplaceBaseUrl: string | undefined
) => {
  if (!marketplaceBaseUrl) return '';
  const { make, model, year } = searchData;
  const marketplaceURL = `${marketplaceBaseUrl}?makes=["${make}"]&maxYear=${year}&minYear=${year}&models=["${model}"]`;
  return marketplaceURL;
};
